import * as React from 'react';
import {
  FilterMenuShell,
  FilterPill,
  MenuRow,
} from 'src/components/FilterDropdown';
import Popover from 'src/Layer/Popover';

export type AmountFilterState = {
  /** Minimum magnitude in dollars, or null for no lower bound. */
  minAmount: number | null;
  /** Maximum magnitude in dollars, or null for no upper bound. */
  maxAmount: number | null;
};

/** Decode an amount bound from a URL query value; anything else is null. */
export const parseAmountParam = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** The cents the query's minAmount/maxAmount arguments expect, from dollars. */
export const amountArg = (dollars: number | null): number | undefined =>
  dollars == null ? undefined : Math.round(dollars * 100);

// A bound is a whole, positive number of cents. Quantizing with the same
// float step amountArg takes keeps the pill's label and the submitted bound
// agreeing on sub-cent input (1.005 would format as $1.01 but round to 100
// cents), and a bound of zero or less excludes nothing as a min and
// everything but zero-amount rows as a max — neither a filter anyone means.
const quantizedBound = (dollars: number | null): number | null => {
  if (dollars == null) return null;
  const cents = Math.round(dollars * 100);
  return cents > 0 ? cents / 100 : null;
};

/**
 * Clamp bounds to whole positive cents and swap a backwards pair. The pill's
 * own inputs commit through this, but a hand-edited URL or saved filters
 * bypass it and the server rejects a backwards range outright rather than
 * guessing — so every consumer normalizes with this before building a query.
 */
export const normalizeAmountBounds = (
  value: AmountFilterState,
): AmountFilterState => {
  const minAmount = quantizedBound(value.minAmount);
  const maxAmount = quantizedBound(value.maxAmount);
  return minAmount != null && maxAmount != null && minAmount > maxAmount
    ? {minAmount: maxAmount, maxAmount: minAmount}
    : {minAmount, maxAmount};
};

// Round thresholds for one-click noise floors; anything else is two keystrokes
// away in the custom inputs.
const PRESETS = [100, 500, 1000, 5000, 10000];

// Fixed en-US formatting rather than the runtime locale, so the pill (and
// screenshot baselines) render the same everywhere. Whole dollars drop the
// cents: the label reads "≥ $10,000", not "≥ $10,000.00".
//
// The $ is a labeling convenience, not a conversion: the server compares
// bounds against each row's amount at face value in its own currency (like
// the amountOver: search token), so on a wallet row the number is yen or
// miles. Accounts are overwhelmingly USD and a noise-floor/big-movers filter
// tolerates that edge — a deliberate call, not an oversight.
const formatDollars = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);

const isEmpty = (value: AmountFilterState): boolean =>
  value.minAmount == null && value.maxAmount == null;

// A range collapsed onto one number: "show me the $42.30 charge". Expressed
// as equal bounds rather than its own filter shape, so the query arguments,
// the URL, and the server's inclusive comparison are all unchanged — the
// shortcut is in the UI, not in the data.
const isExact = (value: AmountFilterState): boolean =>
  value.minAmount != null && value.minAmount === value.maxAmount;

// A bound the user typed: empty (or zero, which excludes nothing) reads as no
// bound at all rather than a filter for exactly-zero rows.
const parseDraft = (draft: string): number | null => {
  const trimmed = draft.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

type Props = {
  value: AmountFilterState;
  onChange: (value: AmountFilterState) => void;
};

/**
 * Magnitude filter pill for transaction lists: bounds compare the absolute
 * value of each row's amount, so one filter catches both legs of a movement
 * (≥ $10,000 surfaces the +$10k and the -$10k side of a transfer alike) or
 * mutes small-ticket noise without hiding refunds. Direction already has its
 * own filters where it matters; this one is deliberately sign-blind. The menu
 * offers one-click "≥ $X" floors, with min/max inputs in the footer for
 * ranges and ceilings — committed on blur or Enter, so typing a threshold
 * doesn't fire a query per keystroke. Bounds are in dollars; consumers
 * convert to the cents the query arguments expect via `amountArg`.
 */
export const AmountFilterSelect = ({value, onChange}: Props) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  // Free-typed text, committed as bounds on blur/Enter. Re-seeded from the
  // committed value on every open and after every commit, so an outside reset
  // (e.g. the Transactions header's Reset filters) can't leave stale digits
  // behind the closed menu, and the two rows never disagree about what is
  // applied.
  const [exactDraft, setExactDraft] = React.useState('');
  const [minDraft, setMinDraft] = React.useState('');
  const [maxDraft, setMaxDraft] = React.useState('');

  const seedDrafts = (v: AmountFilterState) => {
    setExactDraft(isExact(v) ? String(v.minAmount) : '');
    setMinDraft(v.minAmount != null ? String(v.minAmount) : '');
    setMaxDraft(v.maxAmount != null ? String(v.maxAmount) : '');
  };

  const changeOpen = (open: boolean) => {
    if (open) {
      seedDrafts(value);
    }
    setMenuOpen(open);
  };

  const apply = (bounds: AmountFilterState) => {
    // A backwards range is an obvious slip of the keys; swapping beats
    // erroring inside a popover with no room for a message.
    const {minAmount, maxAmount} = normalizeAmountBounds(bounds);
    seedDrafts({minAmount, maxAmount});
    if (minAmount !== value.minAmount || maxAmount !== value.maxAmount) {
      onChange({minAmount, maxAmount});
    }
  };

  const commitRange = () => {
    apply({minAmount: parseDraft(minDraft), maxAmount: parseDraft(maxDraft)});
  };

  // The exact box is a shortcut that writes both bounds at once. An empty one
  // commits nothing rather than clearing: this fires on every blur, including
  // tabbing down to the range inputs, and a filter that erased itself on the
  // way past would be worse than no shortcut. "Any amount" is how you clear.
  const commitExact = () => {
    const amount = parseDraft(exactDraft);
    if (amount == null) return;
    apply({minAmount: amount, maxAmount: amount});
  };

  const handleKeyDown =
    (commit: () => void) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        commit();
        setMenuOpen(false);
      }
    };

  const selectPreset = (preset: number) => {
    onChange({minAmount: preset, maxAmount: null});
    changeOpen(false);
  };

  const clear = () => {
    onChange({minAmount: null, maxAmount: null});
    changeOpen(false);
  };

  const inputClassName =
    'border-border bg-background text-foreground w-24 min-w-0 flex-1 rounded border px-2 py-1 text-sm';

  // Fixed-width row labels so the exact box and the range pair line up, and
  // so neither row needs a heading above it.
  const rowLabelClassName = 'text-muted-foreground w-11 shrink-0 text-sm';

  const menu = (
    <FilterMenuShell
      footer={
        <div className="flex flex-col gap-2">
          {/* Looking for one known charge is the other common reason to reach
              for this filter, and expressing it as a range means typing the
              same number twice. */}
          <div className="flex items-center gap-2">
            <span className={rowLabelClassName}>Exact</span>
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              placeholder="Amount $"
              aria-label="Exact amount in dollars"
              value={exactDraft}
              onChange={(e) => setExactDraft(e.target.value)}
              onBlur={commitExact}
              onKeyDown={handleKeyDown(commitExact)}
              className={inputClassName}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className={rowLabelClassName}>Range</span>
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              placeholder="Min $"
              aria-label="Minimum amount in dollars"
              value={minDraft}
              onChange={(e) => setMinDraft(e.target.value)}
              onBlur={commitRange}
              onKeyDown={handleKeyDown(commitRange)}
              className={inputClassName}
            />
            <span className="text-muted-foreground text-sm">to</span>
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              placeholder="Max $"
              aria-label="Maximum amount in dollars"
              value={maxDraft}
              onChange={(e) => setMaxDraft(e.target.value)}
              onBlur={commitRange}
              onKeyDown={handleKeyDown(commitRange)}
              className={inputClassName}
            />
          </div>
        </div>
      }
    >
      <div className="flex min-h-0 flex-col overflow-y-auto py-1">
        <MenuRow
          selected={isEmpty(value)}
          onClick={clear}
          label="Any amount"
        />
        {PRESETS.map((preset) => (
          <MenuRow
            key={preset}
            selected={value.minAmount === preset && value.maxAmount == null}
            onClick={() => selectPreset(preset)}
            label={`≥ ${formatDollars(preset)}`}
          />
        ))}
      </div>
    </FilterMenuShell>
  );

  const selectionLabel = isExact(value)
    ? // Not "$42.30–$42.30": a collapsed range is one amount, and reading it
      // back as a range would look like a bug in the pill.
      `= ${formatDollars(value.minAmount ?? 0)}`
    : value.minAmount != null && value.maxAmount != null
      ? `${formatDollars(value.minAmount)}–${formatDollars(value.maxAmount)}`
      : value.minAmount != null
        ? `≥ ${formatDollars(value.minAmount)}`
        : value.maxAmount != null
          ? `≤ ${formatDollars(value.maxAmount)}`
          : null;

  return (
    <Popover
      open={menuOpen}
      popoverContents={menu}
      popoverAnchor="bottom-left"
      onOutsideClick={() => changeOpen(false)}
    >
      {(anchorRef: React.RefCallback<HTMLButtonElement>) => (
        <FilterPill
          anchorRef={anchorRef}
          onClick={() => changeOpen(!menuOpen)}
          label="Amount"
          value={selectionLabel}
          active={selectionLabel != null}
        />
      )}
    </Popover>
  );
};
